/**
 * apps/web :: components/whatsnext :: InsuranceApplyHowToSection
 *
 * Section 3 of the post-pass journey (ROADS-431).
 *
 * Practical step-by-step for actually getting insured:
 *   1. Gather documents
 *   2. Compare 3-5 quotes
 *   3. Compare coverage / premium / deductible
 *   4. Bind the policy
 *   5. Get proof of insurance for registration
 *
 * Static, evergreen content — no per-state branching. State-specific
 * minimums live in Section 2 (Insurance 101).
 *
 * Status-agnostic per ROADS-436: copy describes what every applicant
 * does (gather these documents, request these quotes), never user labels.
 */
import { useTranslation } from "react-i18next";

const STEP_KEYS = [
  "gatherDocuments",
  "requestQuotes",
  "compareCoverage",
  "bindPolicy",
  "proofForRegistration",
] as const;

export default function InsuranceApplyHowToSection() {
  const { t } = useTranslation();
  return (
    <section
      data-testid="whatsnext-section-insurance-howto"
      aria-labelledby="whatsnext-insurance-howto-heading"
      className="rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
    >
      <h2
        id="whatsnext-insurance-howto-heading"
        className="font-display text-xl font-bold text-brand-primary"
      >
        {t("whatsnext.insuranceHowTo.heading")}
      </h2>
      <p className="mt-sm text-neutral-700">
        {t("whatsnext.insuranceHowTo.intro")}
      </p>

      <ol
        className="mt-md space-y-md"
        data-testid="whatsnext-insurance-howto-steps"
      >
        {STEP_KEYS.map((key, idx) => (
          <li
            key={key}
            className="rounded-md border border-neutral-200 bg-slate-50 p-sm"
            data-testid={`whatsnext-howto-step-${key}`}
          >
            <p className="text-xs font-semibold uppercase tracking-widest text-neutral-600">
              {t("whatsnext.insuranceHowTo.stepLabel", { n: idx + 1 })}
            </p>
            <h3 className="mt-xs font-semibold text-brand-primary">
              {t(`whatsnext.insuranceHowTo.steps.${key}.title`)}
            </h3>
            <p className="mt-xs text-sm text-neutral-800">
              {t(`whatsnext.insuranceHowTo.steps.${key}.body`)}
            </p>
          </li>
        ))}
      </ol>
    </section>
  );
}
